// The cars team's screen — the lot, and nothing else.
//
// A LIST, one car per row, because that is how a lot is read: down the page,
// eyes on the money column. The actions that matter live ON the row —
// Book / Sell on an available car, the money and both PDFs on a deal — and
// everything second-rank folds into the row's ⋯ menu.
//
// What is deliberately NOT here: the CAP↔Cars float and the company-wide
// totals. Those need the superadmin login; this token cannot reach them.

import React, { useCallback, useEffect, useState } from 'react';
import {
  FiCheckCircle,
  FiDownload,
  FiEdit3,
  FiKey,
  FiMoreVertical,
  FiPhone,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiTag,
  FiTrash2,
  FiTruck,
} from 'react-icons/fi';
import CarsWorkLayout from '../components/layout/CarsWorkLayout.jsx';
import { Alert, Button, DeleteConfirm } from '../components/common/index.js';
import AddCarModal from '../components/cars/AddCarModal.jsx';
import SellCarModal from '../components/cars/SellCarModal.jsx';
import CarInquiries, { InquiryForm } from '../components/cars/CarInquiries.jsx';
import EditCarSaleModal from '../components/cars/EditCarSaleModal.jsx';
import CarMoneyModal from '../components/cars/CarMoneyModal.jsx';
import {
  errText,
  fmtMoney,
  fmtShort,
  searchIconCls,
  searchInputCls,
} from '../components/cars/controls.jsx';
import {
  downloadBookingNote, downloadDeliveryNote, whatsAppBookingNote, whatsAppDeliveryNote,
} from '../components/cars/noteActions.js';
import { carsTeamApi } from '../api/carsApi';

const TABS = [
  { value: 'available', label: 'Available' },
  { value: 'booked', label: 'Booked' },
  { value: 'sold', label: 'Sold' },
  { value: '', label: 'All' },
];

const STATUS_TONE = {
  available: 'bg-emerald-50 text-emerald-700',
  booked: 'bg-amber-50 text-amber-700',
  sold: 'bg-slate-100 text-slate-600',
  returned: 'bg-rose-50 text-rose-700',
};

const Tile = ({ label, value, hint, tone = 'slate' }) => {
  const tones = {
    violet: 'border-violet-200 bg-violet-50 text-violet-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    slate: 'border-slate-200 bg-white text-slate-700',
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <p className="text-[10px] font-bold uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-2xl font-black tabular-nums">{value}</p>
      {hint && <p className="text-[11px] opacity-70">{hint}</p>}
    </div>
  );
};

// ── The row's second-rank actions, folded away ───────────────────────────────
const RowMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="More"
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50"
      >
        <FiMoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => { setOpen(false); item.onClick(); }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold transition hover:bg-slate-50 ${
                  item.tone === 'danger' ? 'text-rose-600' : 'text-slate-600'
                }`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// A small labelled row action — the visual middleweight between Button and menu.
const RowBtn = ({ onClick, disabled, tone = 'slate', title, children }) => {
  const tones = {
    slate: 'border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700',
    violet: 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    amber: 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100',
    wa: 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition disabled:opacity-50 ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

// ── One car, one row ─────────────────────────────────────────────────────────
const CarRow = ({
  car, busy, onBook, onSell, onEdit, onDelete, onEditSale, onMoney,
  onUndoSale, onAsk, onPaper,
}) => {
  const sale = car.sale;
  const consignment = car.ownership === 'consignment';
  const owing = sale && Number(sale.balance_due) > 0;
  const delivered = Boolean(sale?.delivered);
  const asks = car.inquiries || [];
  const firstAsk = asks[0];

  // Everything that is not the row's main job folds in here.
  const menu = [
    { label: 'Someone asked', icon: <FiPhone className="h-3.5 w-3.5" />, onClick: () => onAsk(car) },
    { label: 'Edit car', icon: <FiEdit3 className="h-3.5 w-3.5" />, onClick: () => onEdit(car) },
    ...(sale ? [
      { label: 'The money', icon: <FiCheckCircle className="h-3.5 w-3.5" />, onClick: () => onMoney(car) },
      { label: 'Edit sale', icon: <FiEdit3 className="h-3.5 w-3.5" />, onClick: () => onEditSale(car) },
      { label: 'Undo sale', icon: <FiTrash2 className="h-3.5 w-3.5" />, tone: 'danger', onClick: () => onUndoSale(car) },
    ] : [
      { label: 'Delete car', icon: <FiTrash2 className="h-3.5 w-3.5" />, tone: 'danger', onClick: () => onDelete(car) },
    ]),
  ];

  return (
    <div className="px-4 py-3 transition hover:bg-slate-50/60">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* The car */}
        <div className="min-w-[200px] flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-sm font-black text-slate-900">{car.display_name}</p>
            <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${STATUS_TONE[car.status] || 'bg-slate-100 text-slate-600'}`}>
              {delivered ? 'Delivered' : car.status_label}
            </span>
            {consignment && (
              <span className="shrink-0 rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold text-violet-700"
                title={`${car.owner_name}'s car · ${car.commission_label}`}>
                {car.owner_name}'s
              </span>
            )}
            {asks.length > 0 && (
              <button type="button" onClick={() => onAsk(car)}
                title={firstAsk ? `${firstAsk.customer_name} — ${firstAsk.note || firstAsk.customer_phone}` : ''}
                className="shrink-0 rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold text-violet-700 hover:bg-violet-100">
                📌 {asks.length} asked
              </button>
            )}
          </div>
          <p className="mt-0.5 truncate text-[11px] text-slate-400">
            {[car.registration_number, car.variant_label,
              car.km_driven ? `${Number(car.km_driven).toLocaleString('en-IN')} km` : '',
              car.color].filter(Boolean).join(' · ')}
          </p>
        </div>

        {/* The money — the column the page is read down */}
        <div className="w-36 shrink-0 text-right">
          <p className="text-sm font-black tabular-nums text-slate-900">
            {fmtMoney(sale ? sale.sold_price : car.asking_price)}
          </p>
          {!sale && (
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">asking</p>
          )}
          {sale && owing && (
            <p className="text-[11px] font-bold text-amber-600">{fmtMoney(sale.balance_due)} owed</p>
          )}
          {sale && !owing && (
            <p className={`text-[11px] font-bold ${delivered ? 'text-emerald-600' : 'text-violet-600'}`}>
              {delivered ? `delivered ${sale.delivered_at_display || ''}` : 'paid · to deliver'}
            </p>
          )}
          {sale && (
            <p className="truncate text-[10px] text-slate-400">
              {sale.customer_name || 'Buyer'} · {sale.sold_at_display}
            </p>
          )}
        </div>

        {/* The row's own actions */}
        <div className="flex shrink-0 items-center gap-1.5">
          {!sale && car.status !== 'returned' && (
            <>
              <RowBtn tone="amber" disabled={busy} onClick={() => onBook(car)}
                title="A token holds it">
                <FiKey className="h-3 w-3" /> Book
              </RowBtn>
              <RowBtn tone="violet" disabled={busy} onClick={() => onSell(car)}>
                <FiTag className="h-3 w-3" /> Sell
              </RowBtn>
            </>
          )}
          {sale && owing && (
            <>
              <RowBtn tone="emerald" disabled={busy} onClick={() => onMoney(car)}>
                <FiCheckCircle className="h-3 w-3" /> Money arrived
              </RowBtn>
              <RowBtn tone="wa" disabled={busy} onClick={() => onPaper('wa-booking', car)}
                title="WhatsApp the booking note">
                <FiSend className="h-3 w-3" /> WhatsApp
              </RowBtn>
              <RowBtn disabled={busy} onClick={() => onPaper('pdf-booking', car)}
                title="Booking confirmation PDF">
                <FiDownload className="h-3 w-3" /> PDF
              </RowBtn>
            </>
          )}
          {sale && !owing && !delivered && (
            <RowBtn tone="violet" disabled={busy} onClick={() => onMoney(car)}
              title="Hand it over — makes the delivery note">
              <FiTruck className="h-3 w-3" /> Deliver
            </RowBtn>
          )}
          {sale && delivered && (
            <>
              <RowBtn tone="wa" disabled={busy} onClick={() => onPaper('wa-delivery', car)}
                title="WhatsApp the delivery note">
                <FiSend className="h-3 w-3" /> WhatsApp
              </RowBtn>
              <RowBtn disabled={busy} onClick={() => onPaper('pdf-delivery', car)}
                title="Delivery note PDF">
                <FiDownload className="h-3 w-3" /> PDF
              </RowBtn>
            </>
          )}
          <RowMenu items={menu} />
        </div>
      </div>
    </div>
  );
};

const CarsWorkPage = () => {
  const [tab, setTab] = useState('available');
  const [query, setQuery] = useState('');
  const [cars, setCars] = useState([]);
  const [stock, setStock] = useState({});
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [deletingCar, setDeletingCar] = useState(null);
  const [selling, setSelling] = useState(null);
  // The lot, or the calls about it.
  const [view, setView] = useState('lot');
  const [editingSale, setEditingSale] = useState(null);
  const [moneyFor, setMoneyFor] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const [askingAbout, setAskingAbout] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Shown after a car is added when people were waiting for exactly that car.
  const [waiting, setWaiting] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inv, sum] = await Promise.all([
        carsTeamApi.getInventory({
          status: tab || undefined,
          q: query.trim() || undefined,
        }),
        carsTeamApi.getSummary(),
      ]);
      setCars(inv.data.cars || []);
      setStock(inv.data.stock || {});
      setSummary(sum.data || null);
    } catch (e) {
      setError(errText(e, 'Could not load the lot.'));
    } finally {
      setLoading(false);
    }
  }, [tab, query]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    carsTeamApi.getCatalog()
      .then(({ data }) => setCatalog(Array.isArray(data) ? data : data?.brands || []))
      .catch(() => setCatalog([]));   // the picker still takes typed cars
  }, []);

  const undoSale = async (reason) => {
    const car = undoing;
    setBusyId(car.id);
    setError('');
    try {
      await carsTeamApi.undoSale(car.id, reason);
      setUndoing(null);
      load();
    } catch (e) {
      setError(errText(e, 'Could not undo the sale.'));
    } finally {
      setBusyId(null);
    }
  };

  const deleteCar = async (reason) => {
    const car = deletingCar;
    setBusyId(car.id);
    setError('');
    try {
      await carsTeamApi.removeCar(car.id, reason);
      setDeletingCar(null);
      load();
    } catch (e) {
      setError(errText(e, 'Could not remove the car.'));
    } finally {
      setBusyId(null);
    }
  };

  // Adding a car answers the calls nobody could answer before — say so at
  // the moment it happens, while the car is still in the person's hands.
  const onCarAdded = (created) => {
    setWaiting(created?.waiting_customers || []);
    load();
  };

  // The row's paper buttons — booking / delivery note, as PDF or WhatsApp.
  const paper = async (kind, car) => {
    setBusyId(car.id);
    setError('');
    try {
      if (kind === 'wa-booking') await whatsAppBookingNote(car);
      else if (kind === 'pdf-booking') await downloadBookingNote(car);
      else if (kind === 'wa-delivery') whatsAppDeliveryNote(car);
      else await downloadDeliveryNote(car);
    } catch (e) {
      setError(e?.response ? errText(e, 'Could not prepare the note.')
        : (e?.message || 'Could not prepare the note.'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <CarsWorkLayout>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">
            {view === 'lot' ? 'The lot' : 'Enquiries'}
          </h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {view === 'lot'
              ? 'Every car we are holding. Add one when it comes in, sell it when it goes.'
              : 'Who rang about what — and who to ring back now their car is here.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {view === 'lot' && (
            <>
              <button
                onClick={load}
                disabled={loading}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-500 transition-all hover:bg-slate-50 disabled:opacity-60"
              >
                <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <Button onClick={() => setAddOpen(true)}>
                <FiPlus className="mr-1.5 h-4 w-4" /> Add car
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Two jobs on this screen: the cars, and the calls about them. */}
      <div className="mb-5 inline-flex rounded-xl border border-slate-200 bg-white p-1">
        {[{ v: 'lot', l: 'The lot' }, { v: 'calls', l: 'Enquiries' }].map((v) => (
          <button
            key={v.v}
            type="button"
            onClick={() => setView(v.v)}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition ${
              view === v.v ? 'bg-violet-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            {v.l}
          </button>
        ))}
      </div>

      {error && <Alert type="error" message={error} className="mb-4" />}

      {view === 'calls' && <CarInquiries catalog={catalog} />}

      {view === 'lot' && (
      <>
      {/* The car just added is the answer to somebody's call. */}
      {waiting.length > 0 && (
        <div className="mb-5 rounded-2xl border border-emerald-300 bg-emerald-50 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-black text-emerald-800">
                {waiting.length} {waiting.length === 1 ? 'person was' : 'people were'} waiting for this car
              </p>
              <div className="mt-2 space-y-1">
                {waiting.map((w) => (
                  <p key={w.id} className="text-sm text-emerald-900">
                    <span className="font-bold">{w.customer_name}</span>
                    <a href={`tel:${w.customer_phone}`} className="ml-2 font-bold hover:underline">
                      {w.customer_phone}
                    </a>
                    {w.note ? <span className="text-emerald-700/80"> — “{w.note}”</span> : ''}
                  </p>
                ))}
              </div>
              <p className="mt-2 text-xs font-semibold text-emerald-700/80">
                Ring them before someone else does — the Enquiries tab keeps the list.
              </p>
            </div>
            <button type="button" onClick={() => setWaiting([])}
              className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-100">
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile
          tone="violet"
          label="On the lot"
          value={(stock.available || 0) + (stock.booked || 0)}
          hint={`${stock.booked || 0} booked`}
        />
        <Tile label="Asking value" value={fmtShort(stock.asking_value)} hint="of the unsold cars" />
        <Tile
          tone="emerald"
          label="Sold today"
          value={summary?.today?.sold_count ?? 0}
          hint={fmtShort(summary?.today?.gross_value)}
        />
        <Tile
          tone={Number(summary?.today?.balance_due_value) > 0 ? 'amber' : 'slate'}
          label="To collect"
          value={fmtShort(summary?.today?.balance_due_value)}
          hint="from today's buyers"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t.value || 'all'}
              type="button"
              onClick={() => setTab(t.value)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                tab === t.value ? 'bg-violet-600 text-white' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[200px] flex-1">
          <FiSearch className={searchIconCls} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search car, registration, owner…"
            className={searchInputCls}
          />
        </div>
      </div>

      {loading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-white" />)}
        </div>
      )}

      {!loading && cars.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
            <FiKey className="h-6 w-6 text-slate-400" />
          </div>
          <p className="text-sm font-bold text-slate-700">No cars here</p>
          <p className="mx-auto mt-1 max-w-sm text-xs text-slate-400">
            {tab === 'available'
              ? 'Nothing on the lot right now. Add a car and it shows up here.'
              : 'Nothing matches this filter.'}
          </p>
        </div>
      )}

      {!loading && cars.length > 0 && (
        <div className="divide-y divide-slate-100 overflow-visible rounded-2xl border border-slate-200 bg-white shadow-sm">
          {cars.map((car) => (
            <CarRow
              key={car.id}
              car={car}
              busy={busyId === car.id}
              onBook={(c) => setSelling({ car: c, intent: 'book' })}
              onSell={(c) => setSelling({ car: c, intent: 'sell' })}
              onEdit={setEditingCar}
              onDelete={setDeletingCar}
              onEditSale={setEditingSale}
              onMoney={setMoneyFor}
              onUndoSale={setUndoing}
              onAsk={setAskingAbout}
              onPaper={paper}
            />
          ))}
        </div>
      )}
      </>
      )}

      <AddCarModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        catalog={catalog}
        onSaved={onCarAdded}
        createFn={carsTeamApi.addCar}
      />
      {/* The same form, correcting instead of adding */}
      <AddCarModal
        open={Boolean(editingCar)}
        onClose={() => setEditingCar(null)}
        catalog={catalog}
        onSaved={load}
        car={editingCar}
        saveFn={(payload) => carsTeamApi.updateCar(editingCar.id, payload)}
      />
      {/* Book and Sell are the same record through two doors — the intent
          just decides what the form assumes first. */}
      <SellCarModal
        open={Boolean(selling)}
        onClose={() => setSelling(null)}
        car={selling?.car || null}
        intent={selling?.intent || 'sell'}
        onSold={load}
        sellFn={carsTeamApi.sell}
      />
      <EditCarSaleModal
        open={Boolean(editingSale)}
        car={editingSale}
        onClose={() => setEditingSale(null)}
        onSaved={load}
      />
      {/* Receipts, the loan's bank, delivery + the WhatsApp note */}
      <CarMoneyModal
        open={Boolean(moneyFor)}
        car={moneyFor}
        onClose={() => setMoneyFor(null)}
        onChanged={load}
      />
      {/* "Someone asked about this car" — the pinned note half of enquiries */}
      <InquiryForm
        open={Boolean(askingAbout)}
        car={askingAbout}
        catalog={catalog}
        onClose={() => setAskingAbout(null)}
        onSaved={load}
      />
      {deletingCar && (
        <DeleteConfirm
          title="Remove this car?"
          summary={`${deletingCar.display_name}${deletingCar.registration_number ? ` · ${deletingCar.registration_number}` : ''}`}
          consequences={[
            'The car comes off the lot completely, along with its costs.',
            'The superadmin sees this in Deleted Records, under your name.',
          ]}
          busy={Boolean(busyId)}
          onClose={() => setDeletingCar(null)}
          onConfirm={({ reason }) => deleteCar(reason)}
        />
      )}
      {undoing && (
        <DeleteConfirm
          title="Undo this sale?"
          summary={`${undoing.display_name} — sold to ${undoing.sale?.customer_name || 'a buyer'} for ${fmtMoney(undoing.sale?.sold_price)}`}
          consequences={[
            'The car goes back on the lot as available.',
            'The sale is removed, along with the money it recorded.',
            'The superadmin sees this in Deleted Records, under your name.',
          ]}
          busy={Boolean(busyId)}
          onClose={() => setUndoing(null)}
          onConfirm={({ reason }) => undoSale(reason)}
        />
      )}
    </CarsWorkLayout>
  );
};

export default CarsWorkPage;
